<template>
  <div class="rm-screen rm-go">
    <div v-if="loading && !hasOrders" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <div v-else-if="failed && !hasOrders" class="rm-empty rm-go__error" role="alert">
      <span>{{ $t("report_mobile.load_failed") }}</span>
      <button type="button" class="rm-btn rm-btn--sm" @click="$emit('retry')">
        <v-icon size="18" color="#1c1917">mdi-refresh</v-icon>
        {{ $t("report_mobile.retry") }}
      </button>
    </div>

    <template v-else>
      <div v-if="hasOrders" class="rm-stats">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("orders") }}</span>
          <span class="rm-stat__value rm-num">{{ visibleCount }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("total") }}</span>
          <span class="rm-stat__value rm-stat__value--small rm-num">{{ totalText }}</span>
        </div>
        <div class="rm-stat rm-stat--accent">
          <span class="rm-stat__label">{{ $t("guest_desktop.active") }}</span>
          <span class="rm-stat__value rm-num">{{ activeCount }}</span>
        </div>
      </div>

      <div v-if="statusCounts.length" class="rm-chip-row rm-go__chips" role="group" :aria-label="$t('status')">
        <button
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': !status }"
          :aria-pressed="String(!status)"
          @click="$emit('update:status', '')"
        >
          {{ $t("all") }}
          <b class="rm-num">{{ total }}</b>
        </button>
        <button
          v-for="item in statusCounts"
          :key="item.state"
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': status === item.state }"
          :aria-pressed="String(status === item.state)"
          @click="$emit('update:status', status === item.state ? '' : item.state)"
        >
          <i class="rm-go__dot" :style="dotStyle(item.state, status === item.state)"></i>
          {{ $t(item.state) }}
          <b class="rm-num">{{ item.count }}</b>
        </button>
      </div>

      <div v-if="!dayBlocks.length" class="rm-empty">{{ emptyTitle }}</div>

      <section v-for="day in dayBlocks" :key="day.date" class="rm-go__day">
        <h2 v-if="multiDay" class="rm-section-title rm-go__day-title">
          {{ dayTitle(day.date) }}
          <span class="rm-num">{{ day.summary }}</span>
        </h2>

        <article
          v-for="row in day.rows"
          :key="row.key"
          class="rm-card rm-go-order"
          :class="{ 'is-selected': row.order.id === activeId }"
          @click="open(row, $event)"
        >
          <div class="rm-go-order__head">
            <button
              type="button"
              class="rm-go-order__id rm-num"
              :aria-label="`${$t('order_details')} #${row.order.id}`"
              @click.stop="open(row, $event)"
            >
              #{{ row.order.id }}
            </button>
            <span class="rm-go-order__time rm-num">{{ row.time || "–" }}</span>
            <span v-if="row.asap" class="rm-go-tag">{{ $t("asap") }}</span>
            <span class="rm-go-order__spacer"></span>
            <button
              type="button"
              class="rm-go-badge"
              :class="{ 'is-busy': changingId === row.order.id }"
              :style="badgeStyle(row.state)"
              :aria-label="`${$t('status')}: ${$t(row.state)}`"
              @click.stop="pickStatus(row)"
            >
              <i class="rm-go__dot" :style="dotStyle(row.state)"></i>
              {{ $t(row.state) }}
              <v-icon size="14">mdi-chevron-down</v-icon>
            </button>
          </div>

          <div class="rm-go-order__guest">
            <b>{{ row.displayName }}</b>
            <a v-if="row.guest.phone" class="rm-num" :href="`tel:${row.guest.phone}`" @click.stop>
              <v-icon size="14" color="#9a3412">mdi-phone</v-icon>
              {{ row.guest.phone }}
            </a>
          </div>

          <div class="rm-go-order__delivery">
            <v-icon size="16">{{ row.delivery.icon }}</v-icon>
            <span>
              {{ row.delivery.label }}<template v-if="row.address">, {{ row.address }}</template>
            </span>
          </div>

          <ul class="rm-list rm-go-lines">
            <li v-for="(line, index) in row.lines.slice(0, MAX_LINES)" :key="index">
              <b class="rm-num">{{ line.quantity }}×</b>
              <span>{{ line.name }}</span>
            </li>
            <li v-if="row.lines.length > MAX_LINES" class="rm-muted">
              {{ $t("guest_desktop.more_items", { n: row.lines.length - MAX_LINES }) }}
            </li>
          </ul>

          <div class="rm-go-order__foot">
            <span v-if="isAdmin && row.order.restaurant" class="rm-go-order__restaurant">
              <v-icon size="14" color="#9a3412">mdi-store</v-icon>
              {{ row.order.restaurant.name }}
            </span>
            <span class="rm-go-order__spacer"></span>
            <b class="rm-go-order__amount rm-num">{{ row.amountText }}</b>
          </div>
        </article>
      </section>
    </template>

    <ui-sheet v-model="statusSheet" :title="$t('status')">
      <template #action>
        <span v-if="statusTarget" class="rm-muted rm-num">#{{ statusTarget.order.id }}</span>
      </template>
      <ul class="rm-list rm-actions">
        <li v-for="item in statusMenuItems(statusTarget && statusTarget.state)" :key="item.value">
          <button
            type="button"
            class="rm-actions__row"
            :class="{ 'is-current': statusTarget && statusTarget.state === item.value }"
            :style="statusTarget && statusTarget.state === item.value ? currentStatusStyle(item.value) : null"
            :aria-current="statusTarget && statusTarget.state === item.value ? 'true' : null"
            :disabled="statusTarget && statusTarget.state === item.value"
            @click="applyStatus(item.value)"
          >
            <v-icon size="22" :style="{ color: getStatusColors(item.value).fg }">{{ item.icon }}</v-icon>
            {{ $t(item.value) }}
            <v-icon
              v-if="statusTarget && statusTarget.state === item.value"
              size="20"
              class="rm-go__check"
              :style="{ color: getStatusColors(item.value).fg }"
            >
              mdi-check
            </v-icon>
          </button>
        </li>
      </ul>
    </ui-sheet>

    <prep-time-dialog v-model="prepTimeDialogOpen" @select-minutes="confirmPrepMinutes" @ready-now="confirmReadyNow"></prep-time-dialog>
  </div>
</template>

<script>
import moment from "moment";
import hasGuestOrderStatus from "@/mixins/report/hasGuestOrderStatus";
import PrepTimeDialog from "@/components/guest/PrepTimeDialog.vue";
import { orderTotal } from "@/utils/guestOrderTotal";
import { DELIVERY_TYPES, isActiveState, menuState } from "@/utils/guestOrderStatus";

const MAX_LINES = 3;

/**
 * Narudžbe kupaca na telefonu: brojke, filter statusa (čipovi), kartice narudžbi po danima.
 * Dodir na karticu otvara detalje, dodir na status meni statusa (donja kartica).
 * Ista svojstva i događaji kao GuestOrdersView (računar), pa je ekran isti, a prikaz drugi.
 */
export default {
  name: "GuestMobileOrdersView",
  mixins: [hasGuestOrderStatus],
  components: { PrepTimeDialog },
  props: {
    days: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    failed: { type: Boolean, default: false },
    hasOrders: { type: Boolean, default: false },
    statusCounts: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    status: { type: String, default: "" },
    delivery: { type: Number, default: null },
    deliveryCounts: { type: Array, default: () => [] },
    changingId: { type: [Number, String], default: null },
    activeId: { type: Number, default: null },
    isAdmin: { type: Boolean, default: false },
    search: { type: String, default: "" },
    live: { type: Boolean, default: false },
    liveText: { type: String, default: "" },
  },
  data: () => ({
    MAX_LINES,
    statusSheet: false,
    statusTarget: null,
  }),
  computed: {
    multiDay() {
      return Object.keys(this.days).length > 1;
    },
    dayBlocks() {
      return Object.entries(this.days).map(([date, guests]) => {
        const rows = guests.map((guest) => this.toRow(guest));
        const amount = rows.reduce((sum, row) => sum + row.amount, 0);
        return { date, rows, summary: `${rows.length} · ${amount.toFixed(2)} ${this.dayCurrency(rows)}` };
      });
    },
    allRows() {
      return this.dayBlocks.flatMap((day) => day.rows);
    },
    visibleCount() {
      return this.allRows.length;
    },
    activeCount() {
      return this.allRows.filter((row) => isActiveState(row.order.state)).length;
    },
    totalText() {
      const buckets = {};
      this.allRows.forEach((row) => {
        buckets[row.currency] = (buckets[row.currency] || 0) + row.amount;
      });
      const parts = Object.entries(buckets).map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`);
      return parts.length ? parts.join(" + ") : `0.00 ${this.restaurantCurrency}`;
    },
    emptyTitle() {
      if (this.search) {
        return this.$t("report_mobile.no_results_for", { search: this.search });
      }
      return this.hasOrders ? this.$t("guest_desktop.no_match") : this.$t("no_orders_found");
    },
  },
  methods: {
    toRow(guest) {
      const order = guest.orders_guest[0];
      const amount = orderTotal(order);
      const currency = order?.restaurant?.settings?.price || this.restaurantCurrency;
      const type = DELIVERY_TYPES.find((item) => item.id === order.delivery_type);
      return {
        key: String(order.id),
        guest,
        order,
        state: menuState(order.state),
        time: this.formatOrderTime(order),
        asap: this.isAsapOrder(order),
        displayName: `${this.toLatin(guest.name || "")} ${this.toLatin(guest.lastname || "")}`.trim(),
        amount,
        currency,
        amountText: `${amount.toFixed(2)} ${currency}`,
        delivery: type ? { icon: type.icon, label: this.$t(type.key) } : { icon: "mdi-help-circle-outline", label: "" },
        address: order.delivery_type === 0 ? order.location?.address || "" : "",
        lines: (order.order_guest_transactions || []).map((transaction) => ({
          quantity: transaction.quantity,
          name: this.toLatin(transaction.food?.name || transaction.meals?.name || ""),
        })),
      };
    },
    dayCurrency(rows) {
      const currencies = [...new Set(rows.map((row) => row.currency))];
      return currencies.length === 1 ? currencies[0] : currencies.join("/");
    },
    dayTitle(date) {
      const day = moment(date, "YYYY-MM-DD");
      const weekday = day.toDate().toLocaleDateString(this.$i18n.locale === "en" ? "en" : "sr-Latn", { weekday: "short" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${day.format("DD.MM.")}`;
    },
    badgeStyle(state) {
      const colors = this.getStatusColors(state);
      return { backgroundColor: colors.bg, color: colors.fg };
    },
    dotStyle(state, selected = false) {
      return { backgroundColor: selected ? "#ffffff" : this.getStatusColors(state).fg };
    },
    open(row, event) {
      this.$emit("open", { order: row.order, guest: row.guest, event });
    },
    pickStatus(row) {
      this.statusTarget = row;
      this.statusSheet = true;
    },
    applyStatus(status) {
      const row = this.statusTarget;
      this.statusSheet = false;
      if (row) {
        this.requestStatusChange(row.order, row.guest, status);
      }
    },
  },
};
</script>
